/**
 * 統一 Fetcher 函數
 * 用於 SWR 的資料獲取，統一處理錯誤與認證
 */

import { setRequestId } from '../utils/requestId.js';

/**
 * API 錯誤類別
 */
export class ApiError extends Error {
  status: number;
  message: string;
  details?: unknown;

  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.message = message;
    this.details = details;
  }
}

/**
 * 統一 Fetcher 函數
 *
 * @param url - API 端點 URL
 * @param options - Fetch 選項（包含 headers、method 等）
 * @returns API 回應資料
 * @throws {ApiError} 當 API 回應非 2xx 狀態碼時
 *
 * 注意事項：
 * - 非 2xx 狀態碼必須 throw Error，以便 SWR 的 error 物件能捕捉到
 * - 錯誤必須包含後端回傳的錯誤訊息 (message) 與狀態碼 (status)
 */
export async function fetcher<T = unknown>(
  url: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  // 從 Response Header 讀取 X-Request-ID 並儲存
  const requestId = response.headers.get('X-Request-ID');
  if (requestId) {
    setRequestId(requestId);
  }

  // 處理非 2xx 狀態碼
  if (!response.ok) {
    let errorData: { message?: string; code?: string; details?: unknown } = {};

    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json();
      } else {
        const text = await response.text();
        errorData = { message: text || 'Unknown error' };
      }
    } catch {
      // 無法解析錯誤回應時，使用預設訊息
      errorData = {
        message: `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    // 建立包含狀態碼與錯誤訊息的 Error
    const error = new ApiError(
      response.status,
      errorData.message || `API Error: ${response.status}`,
      errorData.details
    );

    // 保留原始錯誤資料
    (error as any).code = errorData.code;

    throw error;
  }

  // 處理空回應（204 No Content 等）
  if (response.status === 204 || response.status === 205) {
    return null as T;
  }

  // 解析 JSON 回應
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      const text = await response.text();
      return (text ? JSON.parse(text) : {}) as T;
    }
  } catch (parseError) {
    // JSON 解析失敗時，拋出錯誤
    throw new ApiError(
      response.status,
      'Failed to parse response as JSON',
      parseError
    );
  }
}
